/**
 * Loaders for the message parts the build splits out (ADR-WES1,
 * `vite-i18n-split.ts`). `src/i18n.ts` decides when each part loads; this
 * module only knows where they are.
 *
 * In a split build:
 * - English `public` is inlined in the entry (it is what is left of `en`);
 *   `console` and `admin` are `src/i18n.ts?messages=<part>` chunks;
 * - every other locale has a `public` and a `console` chunk
 *   (`src/i18n/locales/<locale>.ts?messages=<part>`); admin copy is English-only.
 *   The head bootstrap can preload a locale's public chunk: its Vite manifest
 *   key is `src/i18n/locales/<locale>.ts?messages=public`.
 *
 * Without the plugin (tsc, Vitest) `en` is whole, and a locale's "public" load
 * is its whole dictionary file, exactly as before the split.
 */
import type { MessagePart } from "./message-parts";

export type Messages = Partial<Record<string, string>>;
export type MessagesLoader = () => Promise<Messages>;

/** Set to `true` by the split plugin's `define`; unset in tsc and Vitest. */
export const messagesSplit: boolean = import.meta.env.TINKR_MESSAGES_SPLIT === true;

const wholeLocales: Record<string, MessagesLoader> = messagesSplit
  ? {}
  : import.meta.glob<Messages>("./locales/*.ts", { import: "default" });
const publicLocales: Record<string, MessagesLoader> = messagesSplit
  ? import.meta.glob<Messages>("./locales/*.ts", { import: "default", query: "?messages=public" })
  : {};
const consoleLocales: Record<string, MessagesLoader> = messagesSplit
  ? import.meta.glob<Messages>("./locales/*.ts", { import: "default", query: "?messages=console" })
  : {};
const englishConsole: Record<string, MessagesLoader> = messagesSplit
  ? import.meta.glob<Messages>("../i18n.ts", { import: "default", query: "?messages=console" })
  : {};
const englishAdmin: Record<string, MessagesLoader> = messagesSplit
  ? import.meta.glob<Messages>("../i18n.ts", { import: "default", query: "?messages=admin" })
  : {};

function only(loaders: Record<string, MessagesLoader>): MessagesLoader | null {
  const values = Object.values(loaders);
  return values.length === 1 ? values[0]! : null;
}

/**
 * The loader for one part of one locale, or `null` when there is nothing to
 * fetch: English public copy is bundled, admin copy is English-only, and source
 * mode keeps English whole.
 */
export function messagePartLoader(locale: string, part: MessagePart): MessagesLoader | null {
  if (locale === "en") {
    if (!messagesSplit || part === "public") return null;
    return only(part === "console" ? englishConsole : englishAdmin);
  }
  if (part === "admin") return null;
  const file = `./locales/${locale}.ts`;
  if (!messagesSplit) return part === "public" ? wholeLocales[file] ?? null : null;
  return (part === "public" ? publicLocales : consoleLocales)[file] ?? null;
}
